import { Component, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LocalStorageService } from 'ngx-webstorage';
import { EMPTY, TimeoutError, forkJoin, of, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  switchMap,
  tap,
  timeout,
} from 'rxjs/operators';
import { EquineService } from '../equine.service';
import { QuoteService } from '../../quote.service';
import { requireEquineQuoteId, resolveEquinePreviousPolicyId } from '../equine-utils';

@Component({
  selector: 'app-equine-quote-view',
  templateUrl: './equine-quote-view.component.html',
  styleUrls: ['./equine-quote-view.component.css']
})
export class EquineQuoteViewComponent implements OnInit {
  private static readonly REFERRED_STATUS_ID = 'f6d14624-6351-4d93-b27b-70fb8a06ec5d';
  private static readonly QUOTED_STATUS_ID = '80c0db90-ba5d-4387-b09a-4c008a664932';
  private static readonly DECLINED_STATUS_ID = 'dda8734c-d149-4336-b8cc-c2e205430752';
  private static readonly EXPIRED_STATUS_ID = 'f1a42f5b-652b-42fc-864b-3462d63078ab';
  private static readonly IN_PROGRESS_STATUS_ID = 'e216564f-552a-4e45-ae67-0a63b7074691';
  
  private static readonly TRANSACTION_LABELS: Record<number, string> = {
    1: 'New Business',
    2: 'Renewal',
    3: 'MTA',
    4: 'Cancellation',
    5: 'Lapsed',
    6: 'NTU',
  };
  quoteId: string;
  quoteDetails: any = {};
  staticData: any = null;
  selectedIndex = 0;
  loading = true;
  converting = false;
  convertingToQuote = false;
  declining = false;
  regenerating = false;
  premiumAdjustmentSaving = false;
  hasImportantNotes: boolean = false;
  totalImpNotes: number = 0;
  isReferred = false;
  isDeclined = false;
  isExpired = false;
  isInProgress = false;

  transactionTypeLabel = '';
  statusLabel = '';
  statusBadgeClass = 'badge-secondary';
  adjustedPremiumDetails: any = null;
  premiumAdjustments: any = {
    premium_loading_percent: null,
    premium_loading_fixed: null,
    premium_loading_reason: '',
    premium_discount_percent: null,
    premium_discount_fixed: null,
    premium_discount_reason: '',
    overwrite_underwriting_fee: null
  };

  isAdmin: boolean = false;
  isBroker = false;
  convertPolicyStartDate: Date | null = null;
  convertModalMinDate: Date = new Date();
  convertModalMaxDate: Date = new Date();
  convertPolicyStartInvalid = false;
  displayEmailView: boolean = false;
  displayNewEmailView: boolean = false;
  email_id: string = '';

  constructor(
    private equineService: EquineService,
    private quoteService: QuoteService,
    private route: ActivatedRoute,
    private router: Router, 
    private modalService: NgbModal,
    private snackBar: MatSnackBar,
    private sessionSt: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.quoteId = this.route.snapshot.params.quote_id;
    if (this.sessionSt.retrieve('is_admin')) {
      this.isAdmin = true;
    }
    if (this.sessionSt.retrieve('is_broker')) {
      this.isBroker = true;
    }
    if (this.quoteId) {
      this.getQuote();
    } else {
      this.loading = false;
    }
  }

  getQuote(): void {
    this.loading = true;
    forkJoin({
      staticRes: this.equineService.getStaticData().pipe(catchError(() => of({ success: false }))),
      quoteRes: this.equineService.getEquineData(this.quoteId).pipe(timeout(15000)),
    })
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: ({ staticRes, quoteRes }: { staticRes: any; quoteRes: any }) => {
          if (staticRes?.success && staticRes?.result) {
            this.staticData = staticRes.result;
          }
          if (quoteRes.success && quoteRes.result) {
            const result = quoteRes.result;
            if (result.is_policy === 1 || result.is_policy === true) {
              this.router.navigate(['/equine', 'policy', this.quoteId], { replaceUrl: true });
              return;
            }
            this.quoteDetails = result;
            this.loadStoredAdjustments();
            this.updateQuoteState();
          }
        },
        error: (error: any) => {
          if (error instanceof TimeoutError || error?.name === 'TimeoutError') {
            this.snackBar.open('Loading quote timed out. Please refresh and try again.', 'Close', { duration: 4000 });
            return;
          }
          this.snackBar.open('Failed to load quote.', 'Close', { duration: 3000 });
        },
      });
  }

  private updateQuoteState(): void {
    this.isReferred = this.quoteDetails?.insurance_status_id === EquineQuoteViewComponent.REFERRED_STATUS_ID;
    this.isDeclined = this.quoteDetails?.insurance_status_id === EquineQuoteViewComponent.DECLINED_STATUS_ID;
    this.isExpired = this.quoteDetails?.insurance_status_id === EquineQuoteViewComponent.EXPIRED_STATUS_ID;
    this.isInProgress = this.quoteDetails?.insurance_status_id === EquineQuoteViewComponent.IN_PROGRESS_STATUS_ID
    
    const txType = Number(this.quoteDetails?.transaction_type) || 0;
    this.transactionTypeLabel = EquineQuoteViewComponent.TRANSACTION_LABELS[txType] || '';

    const statusId = this.quoteDetails?.insurance_status_id;
    if (statusId === EquineQuoteViewComponent.REFERRED_STATUS_ID) {
      this.statusLabel = 'Referred';
      this.statusBadgeClass = 'badge-warning';
    } else if (statusId === EquineQuoteViewComponent.EXPIRED_STATUS_ID) {
      this.statusLabel = 'Expired';
      this.statusBadgeClass = 'badge-dark';
    } else if (statusId === EquineQuoteViewComponent.QUOTED_STATUS_ID) {
      this.statusLabel = 'Quoted';
      this.statusBadgeClass = 'badge-success';
    } else if (statusId === EquineQuoteViewComponent.DECLINED_STATUS_ID) {
      this.statusLabel = 'Declined';
      this.statusBadgeClass = 'badge-danger';
    } else {
      this.statusLabel = 'In Progress';
      this.statusBadgeClass = 'badge-info';
    }
  }

  public convertToQuote(): void {
    this.convertingToQuote = true;
    const data = {
      quote_id: this.quoteId,
    };

    this.quoteService.approveReferredQuote(data).subscribe({
      next: (res: any) => {
        this.convertingToQuote = false;
        if (res.success) {
          this.snackBar.open('Quote status updated to Quoted.', 'Close', { duration: 3000 });
          this.getQuote();
        } else {
          this.snackBar.open(res.message || 'Failed to convert to quote.', 'Close', { duration: 4000 });
        }
      },
      error: () => {
        this.convertingToQuote = false;
        this.snackBar.open('Failed to convert to quote.', 'Close', { duration: 3000 });
      }
    });
  }

  public openDeclineModal(modal: any): void {
    this.modalService.open(modal, { centered: true });
  }

  public declineQuote(modal: any): void {
    this.declining = true;
    const data = {
      quote_id: this.quoteId,
      insurance_status_id: EquineQuoteViewComponent.DECLINED_STATUS_ID
    };
    this.quoteService.updateAnyQuoteStatus(data).subscribe({
      next: (res: any) => {
        this.declining = false;
        modal.dismiss();
        if (res?.success) {
          this.snackBar.open(res.message || 'Quote declined successfully.', 'Close', { duration: 3000 });
          this.getQuote();
          return;
        }
        this.snackBar.open(res?.message || 'Failed to decline quote.', 'Close', { duration: 4000 });
      },
      error: () => {
        this.declining = false;
        this.snackBar.open('Failed to decline quote.', 'Close', { duration: 3000 });
      }
    });
  }

  public editQuote(): void {
    this.router.navigate(['/equine', 'quote', this.quoteId]);
  }

  get isMtaConversion(): boolean {
    return Number(this.quoteDetails?.transaction_type) === 3;
  }

  public openConvertModal(modal: any): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const max = new Date(today);
    max.setDate(max.getDate() + 14);
    this.convertModalMaxDate = max;
    if (this.isBroker) {
      this.convertModalMinDate = new Date(today);
    } else {
      const min = new Date(today);
      min.setFullYear(min.getFullYear() - 10);
      this.convertModalMinDate = min;
    }
    const raw = this.quoteDetails?.policy_start_date;
    const initial = raw ? new Date(raw) : new Date(today);
    initial.setHours(0, 0, 0, 0);
    let t = initial.getTime();
    if (!this.isMtaConversion) {
      const minT = this.convertModalMinDate.getTime();
      const maxT = this.convertModalMaxDate.getTime();
      if (t < minT) {
        t = minT;
      }
      if (t > maxT) {
        t = maxT;
      }
    }
    this.convertPolicyStartDate = new Date(t);
    this.convertPolicyStartInvalid = false;
    this.onConvertPolicyStartChange();
    this.modalService.open(modal, { centered: true });
  }

  onConvertPolicyStartChange(): void {
    if (this.isMtaConversion) {
      this.convertPolicyStartInvalid = false;
      return;
    }
    if (!this.convertPolicyStartDate) {
      this.convertPolicyStartInvalid = false;
      return;
    }
    const d = new Date(this.convertPolicyStartDate);
    d.setHours(0, 0, 0, 0);
    if (this.isBroker) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      this.convertPolicyStartInvalid = d < today;
      return;
    }
    const minT = this.convertModalMinDate.getTime();
    const maxT = this.convertModalMaxDate.getTime();
    const t = d.getTime();
    this.convertPolicyStartInvalid = t < minT || t > maxT;
  }

  public convertToPolicy(modal: any): void {
    if (!this.convertPolicyStartDate) {
      this.snackBar.open('Please select the policy start date.', 'Close', { duration: 4000 });
      return;
    }
    if (!requireEquineQuoteId(this.quoteDetails) && !requireEquineQuoteId({ id: this.quoteId })) {
      this.snackBar.open('Quote ID is required before converting.', 'Close', { duration: 4000 });
      return;
    }
    if (this.isBroker && !this.isMtaConversion) {
      const d = new Date(this.convertPolicyStartDate);
      d.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (d < today) {
        this.convertPolicyStartInvalid = true;
        this.snackBar.open('Policy start date cannot be in the past.', 'Close', { duration: 4000 });
        return;
      }
    }
    const policy_start_date = formatDate(this.convertPolicyStartDate, 'yyyy-MM-dd', 'en-US');
    const savePayload = this.buildConvertPolicyStartSavePayload(policy_start_date);
    this.converting = true;
    this.equineService
      .saveQuote(savePayload)
      .pipe(
        switchMap((saveRes: any) => {
          if (!saveRes?.success) {
            return throwError(saveRes?.message || 'Failed to save quote.');
          }
          if (saveRes.result) {
            this.quoteDetails = saveRes.result;
            this.updateQuoteState();
          }
          const previousPolicyId = resolveEquinePreviousPolicyId(this.quoteDetails);
          return this.equineService.convertToPolicy({
            quote_id: this.quoteId,
            policy_start_date,
            previous_policy_id: previousPolicyId,
          });
        }),
        finalize(() => {
          this.converting = false;
        }),
        catchError((err: any) => {
          const msg =
            typeof err === 'string'
              ? err
              : err?.error?.message ||
                err?.message ||
                'Failed to save or convert.';
          this.snackBar.open(msg, 'Close', { duration: 4000 });
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        modal.dismiss();
        if (res.success) {
          this.snackBar.open('Quote converted to policy successfully.', 'Close', { duration: 3000 });
          this.router.navigate(['/equine', 'policy', this.quoteId]);
        } else {
          this.snackBar.open(res.message || 'Failed to convert to policy.', 'Close', { duration: 4000 });
        }
      });
  }

  private buildConvertPolicyStartSavePayload(policyStartYmd: string): any {
    const payload = JSON.parse(JSON.stringify(this.quoteDetails));
    delete payload.premiums;
    const quoteId = requireEquineQuoteId(this.quoteDetails) || requireEquineQuoteId({ id: this.quoteId });
    if (quoteId) {
      payload.id = quoteId;
    }
    const txn = payload.type ?? payload.transaction_type ?? 1;
    const typeNum = Number(txn);
    payload.transaction_type = typeNum;
    if (typeNum !== 3) {
      payload.policy_start_date = policyStartYmd;
    }
    if (typeNum === 1 || typeNum === 2) {
      const start = new Date(this.convertPolicyStartDate as Date);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setFullYear(end.getFullYear() + 1);
      end.setDate(end.getDate() - 1);
      payload.policy_end_date = formatDate(end, 'yyyy-MM-dd', 'en-US');
    }
    payload.is_quoted =
      payload.insurance_status_id === EquineQuoteViewComponent.QUOTED_STATUS_ID ? 1 : 0;
    if (!Array.isArray(payload.statement_of_facts)) {
      payload.statement_of_facts = [];
    }
    payload.premium_adjustments = this.getPremiumAdjustmentsForSave();
    const previousPolicyId = resolveEquinePreviousPolicyId(this.quoteDetails);
    if (previousPolicyId) {
      payload.previous_policy_id = previousPolicyId;
    }
    return payload;
  }

  regenerateDocument(): void {
    if (!this.quoteId) return;
    this.regenerating = true;
    this.equineService.regenerateDocument(this.quoteId).subscribe(
      (res: any) => {
        this.regenerating = false;
        if (res?.success) {
          this.snackBar.open('Document regenerated successfully.', 'Close', { duration: 3000 });
          this.getQuote();
        } else {
          this.snackBar.open(res?.message || 'Failed to regenerate document.', 'Close', { duration: 4000 });
        }
      },
      () => {
        this.regenerating = false;
        this.snackBar.open('Failed to regenerate document.', 'Close', { duration: 3000 });
      }
    );
  }

  onhasImportantNotesChange(event) {
    this.hasImportantNotes = event;
    const notes = this.quoteDetails?.notes;
    this.totalImpNotes = Array.isArray(notes) ? notes.filter((n: any) => n.is_important == 1).length : 0;
  }

  getEmailView(email_id: string) {
    this.email_id = email_id;
    this.displayEmailView = true;
  }

  newEmailView() {
    this.displayNewEmailView = true;
  }

  backToEmailsList() {
    this.displayEmailView = false;
    this.displayNewEmailView = false;
  }

  onPremiumAdjustmentsChange(adjustments: any): void {
    this.premiumAdjustments = { ...this.premiumAdjustments, ...adjustments };
  }

  onPremiumAdjustmentsSave(adjustments: any): void {
    if (!this.quoteDetails?.id) {
      this.snackBar.open('Quote not loaded.', 'Close', { duration: 3000 });
      return;
    }
    this.premiumAdjustments = { ...this.premiumAdjustments, ...adjustments };
    this.executePremiumAdjustmentsSave(true).subscribe();
  }

  private executePremiumAdjustmentsSave(notifySuccess: boolean) {
    if (!this.quoteDetails?.id) {
      return EMPTY;
    }
    const payload = this.buildPremiumAdjustmentsSavePayload(this.premiumAdjustments);
    const prevPolicyId = resolveEquinePreviousPolicyId(this.quoteDetails);
    const txnType = this.quoteDetails.type || this.quoteDetails.transaction_type;
    const origTxType = this.quoteDetails.original_transaction_type;
    this.premiumAdjustmentSaving = true;
    return this.equineService.saveQuote(payload).pipe(
      tap((response: any) => {
        if (response.success && response.result) {
          this.quoteDetails = response.result;
          if (prevPolicyId) {
            this.quoteDetails.previous_policy_id = prevPolicyId;
          }
          if (txnType) {
            this.quoteDetails.type = txnType;
          }
          if (origTxType) {
            this.quoteDetails.original_transaction_type = origTxType;
          }
          const adjustedSnapshot = this.extractAdjustedPremiumSnapshot(response.result);
          if (adjustedSnapshot) {
            this.adjustedPremiumDetails = adjustedSnapshot;
            this.sessionSt.store(this.getAdjustedPremiumStorageKey(), adjustedSnapshot);
          }
          this.syncPremiumAdjustmentsAfterSave(response.result);
          this.updateQuoteState();
          if (notifySuccess) {
            this.snackBar.open('Premium adjustments saved.', 'Close', { duration: 2500 });
          }
        } else {
          this.snackBar.open(response.message || 'Failed to save adjustments.', 'Close', { duration: 4000 });
        }
      }),
      catchError(() => {
        this.snackBar.open('Failed to save adjustments.', 'Close', { duration: 4000 });
        return EMPTY;
      }),
      finalize(() => {
        this.premiumAdjustmentSaving = false;
      })
    );
  }

  private buildPremiumAdjustmentsSavePayload(adjustments: any): any {
    const payload = JSON.parse(JSON.stringify(this.quoteDetails));
    delete payload.premiums;
    const quoteId = requireEquineQuoteId(this.quoteDetails) || requireEquineQuoteId({ id: this.quoteId });
    if (quoteId) {
      payload.id = quoteId;
    }
    const txn = payload.type ?? payload.transaction_type ?? 1;
    payload.transaction_type = Number(txn);
    payload.premium_adjustments = { ...this.getPremiumAdjustmentsForSave(), ...adjustments };
    payload.is_quoted =   payload.insurance_status_id === EquineQuoteViewComponent.QUOTED_STATUS_ID ? 1 : 0;

    if (!Array.isArray(payload.statement_of_facts)) {
      payload.statement_of_facts = [];
    }
    const previousPolicyId = resolveEquinePreviousPolicyId(this.quoteDetails);
    if (previousPolicyId) {
      payload.previous_policy_id = previousPolicyId;
    }
    return payload;
  }

  private getPremiumAdjustmentsForSave(): Record<string, unknown> {
    const fin = this.quoteDetails?.financial_details || {};
    const adj = this.premiumAdjustments || {};
    const blank = (v: any) => v === null || v === undefined || v === '';
    const pick = (key: string) => {
      const v = blank(adj[key]) ? fin[key] : adj[key];
      return blank(v) ? null : v;
    };
    return {
      premium_loading_percent: pick('premium_loading_percent') ?? fin.premium_loading_percentage ?? null,
      premium_loading_fixed: pick('premium_loading_fixed'),
      premium_loading_reason: pick('premium_loading_reason') ?? '',
      premium_discount_percent: pick('premium_discount_percent'),
      premium_discount_fixed: pick('premium_discount_fixed'),
      premium_discount_reason: pick('premium_discount_reason') ?? '',
      overwrite_underwriting_fee: pick('overwrite_underwriting_fee'),
    };
  }

  private getAdjustmentStorageKey(): string {
    return `equine_quote_adjustments_${this.quoteId}`;
  }

  private getAdjustedPremiumStorageKey(): string {
    return `equine_quote_adjusted_premium_${this.quoteId}`;
  }

  private hasActiveAdjustments(adjustments: any): boolean {
    if (!adjustments || typeof adjustments !== 'object') return false;
    const n = (v: any) => (v == null || v === '' ? 0 : Number(v) || 0);
    return (
      n(adjustments.premium_loading_percent) !== 0 ||
      n(adjustments.premium_loading_fixed) !== 0 ||
      n(adjustments.premium_discount_percent) !== 0 ||
      n(adjustments.premium_discount_fixed) !== 0 ||
      n(adjustments.overwrite_underwriting_fee) !== 0
    );
  }

  private extractAdjustedPremiumSnapshot(result: any): any {
    if (result?.premiums?.data && typeof result.premiums.data === 'object') {
      return { ...result.premiums.data };
    }
    if (result?.financial_details && typeof result.financial_details === 'object') {
      return { ...result.financial_details };
    }
    return null;
  }

  private loadStoredAdjustments(): void {
    const stored = this.sessionSt.retrieve(this.getAdjustmentStorageKey());
    const fromFinancials = this.quoteDetails?.financial_details || {};
    const fromQuote = this.quoteDetails?.premium_adjustments || {};
    this.premiumAdjustments = {
      ...this.premiumAdjustments,
      premium_loading_percent:
        stored?.premium_loading_percent ??
        fromQuote.premium_loading_percent ??
        fromFinancials?.premium_loading_percent ??
        fromFinancials?.premium_loading_percentage ??
        null,
      premium_loading_fixed:
        stored?.premium_loading_fixed ?? fromQuote.premium_loading_fixed ?? fromFinancials?.premium_loading_fixed ?? null,
      premium_loading_reason:
        stored?.premium_loading_reason ?? fromQuote.premium_loading_reason ?? fromFinancials?.premium_loading_reason ?? '',
      premium_discount_percent:
        stored?.premium_discount_percent ?? fromQuote.premium_discount_percent ?? fromFinancials?.premium_discount_percent ?? null,
      premium_discount_fixed:
        stored?.premium_discount_fixed ?? fromQuote.premium_discount_fixed ?? fromFinancials?.premium_discount_fixed ?? null,
      premium_discount_reason:
        stored?.premium_discount_reason ?? fromQuote.premium_discount_reason ?? fromFinancials?.premium_discount_reason ?? '',
      overwrite_underwriting_fee:
        stored?.overwrite_underwriting_fee ??
        fromQuote.overwrite_underwriting_fee ??
        fromFinancials.overwrite_underwriting_fee ??
        null
    };
    const hasActive = this.hasActiveAdjustments(this.premiumAdjustments);
    if (!hasActive) {
      this.adjustedPremiumDetails = null;
      this.sessionSt.clear(this.getAdjustedPremiumStorageKey());
    } else {
      const adjustedSnapshot = this.sessionSt.retrieve(this.getAdjustedPremiumStorageKey());
      this.adjustedPremiumDetails =
        adjustedSnapshot && typeof adjustedSnapshot === 'object' ? adjustedSnapshot : null;
    }
  }

  private storeAdjustments(): void {
    this.sessionSt.store(this.getAdjustmentStorageKey(), this.premiumAdjustments);
  }

  private syncPremiumAdjustmentsAfterSave(result: any): void {
    const fromApi = result?.premium_adjustments;
    if (fromApi && typeof fromApi === 'object' && !Array.isArray(fromApi)) {
      this.premiumAdjustments = { ...this.premiumAdjustments, ...fromApi };
    }
    this.storeAdjustments();
  }
}
